/**
 * @file    useTouchedItems.ts
 * @scope   SHARED
 * @summary Tracks which list items (by id) and whether the list itself were touched by the user.
 * @consumes Nothing domain-specific.
 * @produces useTouchedItems
 * @sideEffects None beyond local state.
 * @notes Used to delay validation errors until the user interacts.
 */
import { useCallback, useState } from 'react';

/**
 * @param initialIds Ids considered touched from the start (e.g. pre-existing items in edit mode).
 */
export const useTouchedItems = (initialIds: string[] = []) => {
  const [touchedIds, setTouchedIds] = useState<Set<string>>(() => new Set(initialIds));
  const [isListTouched, setIsListTouched] = useState(false);

  /** Adds the given ids to the touched set. */
  const markTouched = useCallback((ids: string[]) => {
    setTouchedIds((prev) => new Set([...Array.from(prev), ...ids]));
  }, []);

  /** Flags the list as a whole as touched (e.g. adder blur or item removal). */
  const touchList = useCallback(() => setIsListTouched(true), []);

  return { touchedIds, isListTouched, markTouched, touchList };
};
